import template from './uc-icon-picker.html.twig';
import './uc-icon-picker.scss';

/**
 * uc-icon-picker
 *
 * Tick-box picker for the built-in "Payment Provider Icons" library (used
 * via <component name="uc-icon-picker"> in config.xml, field
 * uspIconsLibrarySelected). Fetches the list of available icon files from
 * PaymentIconsAdminController — merchants just tick which ones to show, no
 * upload required. Complements (doesn't replace) the 7 manual uc-media-upload
 * slots below it in the same card, for anything not covered by the library.
 *
 * Value: JSON string array of selected filenames, e.g. '["paypal.svg"]'.
 * Selection order in the saved value always follows the library's own
 * fixed alphabetical order (see PaymentIconsAdminController::list()) rather
 * than tick order, so the storefront render order matches the admin list
 * order exactly and stays stable regardless of the order merchants happened
 * to click checkboxes in.
 */
Shopware.Component.register('uc-icon-picker', {
    template,

    inject: ['ucPaymentIconsApiService'],

    props: {
        value: {
            type: String,
            required: false,
            default: '[]',
        },
        label: {
            type: String,
            required: false,
            default: '',
        },
        helpText: {
            type: String,
            required: false,
            default: '',
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    emits: ['update:value'],

    data() {
        return {
            available: [],
            isLoading: false,
            loadFailed: false,
        };
    },

    computed: {
        selected() {
            try {
                const parsed = JSON.parse(this.value);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        },

        allSelected() {
            return this.available.length > 0
                && this.available.every(icon => this.selected.includes(icon.file));
        },
    },

    created() {
        this.loadIcons();
    },

    methods: {
        async loadIcons() {
            this.isLoading = true;
            this.loadFailed = false;
            try {
                const icons = await this.ucPaymentIconsApiService.getIcons();
                this.available = Array.isArray(icons) ? icons : [];
            } catch {
                this.available = [];
                this.loadFailed = true;
            } finally {
                this.isLoading = false;
            }
        },

        isChecked(file) {
            return this.selected.includes(file);
        },

        toggle(file) {
            if (this.disabled) return;

            const next = this.isChecked(file)
                ? this.selected.filter(f => f !== file)
                : [...this.selected, file];

            this.emitSelection(next);
        },

        selectAll() {
            if (this.disabled) return;
            this.emitSelection(this.available.map(icon => icon.file));
        },

        selectNone() {
            if (this.disabled) return;
            this.emitSelection([]);
        },

        // Always re-sorts to the library's own fixed order before emitting
        // — see the class docblock on why tick order shouldn't leak into
        // the saved/rendered order.
        emitSelection(fileList) {
            const orderedFiles = this.available
                .map(icon => icon.file)
                .filter(file => fileList.includes(file));

            this.$emit('update:value', JSON.stringify(orderedFiles));
        },
    },
});
